import { API, Auth } from 'aws-amplify';
import * as mutations from '../graphql/mutations';
import { ensureUserReadyForOrganization, waitForPropagation } from './userSyncCheckpoint';

/**
 * Create an organization for the signed-in user and make them its ADMIN owner. Extracted from
 * OrganizationSelector so AccountSetup and the selector share one implementation.
 *
 * Returns a result object; expected failures come back as { ok:false, error }, never as a throw, so
 * callers just render `error`. A template-Lambda failure is non-fatal — the org exists, so it returns
 * ok:true with templatesFailed:true.
 *
 * @param {{name: string, contactEmail: string, contactPhone: string}} fields
 * @returns {Promise<{ok: true, organizationId: string, templatesFailed: boolean} | {ok: false, error: string}>}
 */
export async function createOrganizationForUser({ name, contactEmail, contactPhone }) {
  if (!name || !name.trim()) return { ok: false, error: 'Organization name is required' };
  if (!contactEmail || !contactEmail.trim()) return { ok: false, error: 'Contact email is required' };
  if (!contactPhone || !contactPhone.trim()) return { ok: false, error: 'Contact phone number is required' };

  try {
    const user = await Auth.currentAuthenticatedUser();
    const userEmail = user.attributes.email;

    // Absolutely ensure the user's DB row exists before creating anything that references it.
    const readiness = await ensureUserReadyForOrganization(3, 1500);
    if (!readiness.success) {
      return { ok: false, error: 'User account not ready for organization creation. Please refresh and try again.' };
    }
    const dbUser = readiness.user;
    await waitForPropagation(500);

    const createOrgInput = {
      name,
      owner: user.attributes.sub,
      contactEmail,
      contactPhone,
    };

    // Carry an active billing exemption onto the new org (matches the prior selector behavior).
    if (dbUser.billingExempt === true && dbUser.billingExemptUntil) {
      if (new Date(dbUser.billingExemptUntil) > new Date()) {
        createOrgInput.billingBypass = true;
        createOrgInput.billingBypassExpiry = dbUser.billingExemptUntil;
        createOrgInput.billingBypassGrantedBy = dbUser.billingExemptGrantedBy || 'system';
        createOrgInput.billingBypassReason = `Auto-applied: user ${dbUser.email} is billing-exempt`;
        createOrgInput.preBillingBypassLicenses = 0;
        createOrgInput.purchasedLicenses = 9999;
      }
    }

    const newOrg = await API.graphql({ query: mutations.createOrganization, variables: { input: createOrgInput } });
    const organizationId = newOrg.data.createOrganization.id;

    await API.graphql({
      query: mutations.createOrganizationMember,
      variables: {
        input: {
          organizationID: organizationId,
          email: userEmail,
          userSub: user.attributes.sub,
          status: 'ACTIVE',
          role: 'ADMIN',
        },
      },
    });

    // Default award definitions + email templates. Non-fatal: the org is already usable without them.
    let templatesFailed = false;
    try {
      const lambda = await API.post('apifetchdata', '/awardEmailMutation', { body: { organizationId } });
      if (!lambda || !lambda.success) templatesFailed = true;
    } catch (e) {
      templatesFailed = true;
      console.error('Template setup failed; org created, templates need manual setup:', e);
    }

    localStorage.setItem('newlyCreatedOrgId', organizationId);
    return { ok: true, organizationId, templatesFailed };
  } catch (error) {
    console.error('Error creating organization:', error);
    return { ok: false, error: 'Failed to create organization' };
  }
}
